import { faSparkle } from "@fortawesome/pro-solid-svg-icons";
import { Button, Chip, Stack } from "@mui/material";

import { ActionId } from "@/features/files/types";

import { DataMenuButton, Icon, Tooltip, Typography, useTheme } from "../ui-v2";

export type FileMenuItemGroup =
  | "view"
  | "session"
  | "asset"
  | "forge"
  | "negative"
  | "train";

type DropdownLink = {
  id: string;
  name: string;
  route?: string;
  onClick?: () => void;
  subMenu?: DropdownLink[];
  icon?: JSX.Element;
  colors?: string;
  borderTop?: boolean;
  className?: string;
  pointerEvents?: boolean;
  borderBottom?: boolean;
  closeOnClick?: boolean;
  checkable?: boolean;
  checked?: boolean;
  disabled?: boolean;
  weight?: string;
};

export type SelectionMenuItem = {
  id: ActionId;
  group: FileMenuItemGroup;
  name: string;
  shortName?: string;
  tooltip?: string;
  icon: JSX.Element;
  buttonIcon?: JSX.Element;
  colors: string;
  primaryColorButton?: boolean;
  route?: string;
  onClick?: () => void;
  subMenu?: DropdownLink[];
  closeOnClick?: boolean;
  isPrimary?: boolean;
  isSecondary?: boolean;
  isFooterAction?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  disabledTooltipTitle?: string;
  featured?: boolean;
};

interface SelectionSidebarMainActionButtonProps {
  item: SelectionMenuItem;
}

export const SelectionSidebarMainActionButton = (
  props: SelectionSidebarMainActionButtonProps,
) => {
  const { item } = props;
  const theme = useTheme();

  const commonButtonStyles = {
    justifyContent: "flex-start",
    backgroundColor: theme.palette.secondary[6],
    ".MuiButton-startIcon": {
      color: theme.palette.primary.light,
      fontSize: 16,
      marginRight: theme.spacing(2.5),
      minWidth: "24px",
      display: "flex",
      justifyContent: "center",
    },

    "&&&:hover": {
      backgroundColor: theme.palette.secondary[5],
      boxShadow: "none",
    },
  };

  if (item?.subMenu?.length) {
    return (
      <DataMenuButton
        sx={commonButtonStyles}
        key={`forge-session-item-dropdown-${item?.id}`}
        variant="contained"
        size="large"
        color="secondary"
        fullWidth
        startIcon={item.icon}
        onClick={item.onClick}
        disabled={item.disabled}
        aria-label={item.tooltip}
        items={item.subMenu}
        menuProps={{
          disableScrollLock: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          sx: {
            "& .MuiPaper-root": {
              minWidth: "120px",
            },
          },
        }}
      >
        <Typography color="secondary.1">
          {item.name || item.shortName}
        </Typography>
      </DataMenuButton>
    );
  }

  return (
    <Tooltip
      title={item.tooltip}
      placement="left"
      enterDelay={500}
      enterNextDelay={500}
    >
      <Button
        sx={commonButtonStyles}
        size="large"
        onClick={item.onClick}
        startIcon={item.icon}
        variant="contained"
        color="secondary"
        fullWidth
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1,
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography color="secondary.1">
            {item.name || item.shortName}
          </Typography>
          {item.featured && (
            <Chip
              label="Featured"
              variant="filled"
              color="success"
              size="small"
              icon={<Icon icon={faSparkle} />}
              sx={{
                fontSize: 10,
                fontWeight: "bold",
                textTransform: "uppercase",
              }}
            />
          )}
        </Stack>
      </Button>
    </Tooltip>
  );
};
